import { debounce } from 'lodash';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useGlobalContext } from '../context/GlobalContext';
import { getActiveAgentWithLowestCode, mapLegalUnitCodeToName } from './AgentHelper';

/**
 * This hook makes it easier to not overload API.
 * It will call the API only if the callback function does not change for the specified delayed time.
 * The callback function should be wrapped in useCallBack hook and in its dependencies must be specified the search query
 * Each time function changes the delay resets. This should be used only when calling API onChange event.
 * @param {function} callback - The function which will be called, should be wrapped in a useCallBack React hook
 * @param {number} delay - time interval of the delay in milliseconds
 */
export const useDebouncedEffect = (callback, delay) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    return debounce(() => {
      callbackRef.current();
    }, delay);
  }, [delay]);

  useEffect(() => {
    debouncedCallback();

    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback, callback]);
};

/**
 * Simple hook for calling a function before the component mounts, similar to class components constructor() function
 * @param {function} effect - The function/ effect which will be called
 */
export const useBeforeMount = effect => {
  const hasRun = useRef(false);
  if (!hasRun.current) {
    effect();
    hasRun.current = true;
  }
};

/**
 * Hook gets legalunit name from first active agent or from global context
 */
export const useLegalUnitName = team => {
  const { legalUnit } = useGlobalContext();

  return useMemo(() => {
    return getActiveAgentWithLowestCode(team)?.region?.legalUnitName ?? mapLegalUnitCodeToName(legalUnit);
  }, [team, legalUnit]);
};

/**
 * Loads an external script only once per src.
 * Returns { loading, loaded, error }.
 */
export function useScript(src, { attributes, removeOnUnmount = false } = {}) {
  const [state, setState] = useState({
    loading: !!src,
    loaded: false,
    error: null,
  });

  const normalizedAttributes = useMemo(() => attributes ?? {}, [attributes]);

  useEffect(() => {
    if (!src) {
      setState({ loading: false, loaded: false, error: null });
      return;
    }

    const selector = `script[data-script-src="${src}"]`;
    const existing = document.querySelector(selector);

    if (existing && existing.dataset.loaded === 'true') {
      setState({ loading: false, loaded: true, error: null });
      return;
    }

    if (existing && existing.dataset.loaded !== 'true') {
      const onLoad = () => setState({ loading: false, loaded: true, error: null });
      const onError = () =>
        setState({ loading: false, loaded: false, error: new Error(`Failed to load script: ${src}`) });

      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);

      return () => {
        existing.removeEventListener('load', onLoad);
        existing.removeEventListener('error', onError);
      };
    }

    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.dataset.scriptSrc = src;

    Object.entries(normalizedAttributes).forEach(([key, value]) => {
      script.setAttribute(key, String(value));
    });

    const onLoad = () => {
      script.dataset.loaded = 'true';
      setState({ loading: false, loaded: true, error: null });
    };

    const onError = () => {
      setState({ loading: false, loaded: false, error: new Error(`Failed to load script: ${src}`) });
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);

      if (removeOnUnmount) {
        script.remove();
      }
    };
  }, [src, removeOnUnmount, normalizedAttributes]);

  return state;
}
